import React, { useState } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CodeIcon from '@mui/icons-material/Code';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import CodeExplainer from './CodeExplainer';
import AIChatAssistant from './AIChatAssistant';

const UserCodeSnippetViewer = ({ snippet }) => {
  const [tabValue, setTabValue] = useState(0); // Default to Code Only for users
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  if (!snippet) {
    return null;
  }

  const hasExplanations = snippet.explanations && snippet.explanations.length > 0;
  const hasAnalysis = snippet.aiAnalysis && snippet.aiAnalysis.overallPurpose;

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getSyntaxHighlighterLanguage = (lang) => {
    const languageMap = {
      'javascript': 'javascript',
      'python': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'csharp': 'csharp',
      'ruby': 'ruby',
      'go': 'go',
      'rust': 'rust',
      'swift': 'swift',
      'kotlin': 'kotlin',
      'php': 'php',
      'typescript': 'typescript',
      'html': 'html',
      'css': 'css'
    };
    return languageMap[lang] || 'javascript';
  };

  return (
    <div style={{
      background: '#f7fafc',
      minHeight: '100vh',
      paddingBottom: '40px'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '24px 0'
      }}>
        <div style={{
          maxWidth: '95%',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <CodeIcon style={{ fontSize: '40px', color: '#667eea', marginTop: '4px' }} />
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '600',
                color: '#1a202c',
                margin: '0 0 8px 0'
              }}>
                {snippet.name}
                <span style={{
                  marginLeft: '12px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#667eea',
                  background: '#e3f2fd',
                  padding: '4px 12px',
                  borderRadius: '6px'
                }}>
                  #{snippet.snippetCode}
                </span>
              </h1>
              {snippet.description && (
                <p style={{
                  fontSize: '16px',
                  color: '#718096',
                  margin: '0 0 12px 0'
                }}>
                  {snippet.description}
                </p>
              )}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '4px 12px',
                  background: '#e3f2fd',
                  color: '#1976d2',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {snippet.programmingLanguage.toUpperCase()}
                </span>
                <span style={{
                  padding: '4px 12px',
                  background: snippet.difficulty === 'easy' ? '#d4edda' : 
                            snippet.difficulty === 'intermediate' ? '#fff3cd' : '#f8d7da',
                  color: snippet.difficulty === 'easy' ? '#155724' : 
                         snippet.difficulty === 'intermediate' ? '#856404' : '#721c24',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {snippet.difficulty.charAt(0).toUpperCase() + snippet.difficulty.slice(1)}
                </span>
                {snippet.tags && snippet.tags.length > 0 && snippet.tags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '4px 12px',
                      background: '#f3f4f6',
                      color: '#4b5563',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '95%',
        margin: '24px auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Tabs */}
        <div style={{
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex' }}>
            <button
              onClick={() => setTabValue(0)}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderBottom: tabValue === 0 ? '2px solid #667eea' : '2px solid transparent',
                backgroundColor: 'transparent',
                color: tabValue === 0 ? '#667eea' : '#718096',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                marginBottom: '-2px'
              }}
            >
              <CodeIcon fontSize="small" />
              Code Only
            </button>
            {hasAnalysis && (
              <button
                onClick={() => setTabValue(1)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderBottom: tabValue === 1 ? '2px solid #667eea' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: tabValue === 1 ? '#667eea' : '#718096',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  marginBottom: '-2px'
                }}
              >
                <TipsAndUpdatesIcon fontSize="small" />
                Code Analysis
              </button>
            )}
            {hasExplanations && (
              <button
                onClick={() => setTabValue(4)}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  borderBottom: tabValue === 4 ? '2px solid #667eea' : '2px solid transparent',
                  backgroundColor: 'transparent',
                  color: tabValue === 4 ? '#667eea' : '#718096',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  marginBottom: '-2px'
                }}
              >
                <SlideshowIcon fontSize="small" />
                Interactive Explainer
              </button>
            )}
          </div>
          
          {/* AI Chat Button */}
          <button
            onClick={() => setIsChatbotOpen(!isChatbotOpen)}
            style={{
              padding: '8px 16px',
              margin: '8px 12px',
              border: isChatbotOpen ? '2px solid #667eea' : '1px solid #e2e8f0',
              borderRadius: '8px',
              backgroundColor: isChatbotOpen ? '#667eea' : 'white',
              color: isChatbotOpen ? 'white' : '#667eea',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              boxShadow: isChatbotOpen ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none'
            }}
            title="Ask AI about this code (Ctrl+Shift+O)"
          >
            <SmartToyIcon fontSize="small" />
            AI Assistant
          </button>
        </div>

        <div style={{ padding: tabValue === 4 ? '0' : '20px' }}>
          {/* Tab 0: Code Only */}
          {tabValue === 0 && (
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <button
                  onClick={handleCopy}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#4a5568'
                  }}
                >
                  {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#4a5568'
                  }}
                >
                  {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
                </button>
              </div>
              <SyntaxHighlighter
                language={getSyntaxHighlighterLanguage(snippet.programmingLanguage)}
                style={theme === 'dark' ? vscDarkPlus : oneLight}
                showLineNumbers
                wrapLines
                customStyle={{
                  margin: 0,
                  borderRadius: '8px',
                  fontSize: '18px'
                }}
              >
                {snippet.code}
              </SyntaxHighlighter>
            </div>
          )}

          {/* Tab 1: Code Analysis */}
          {tabValue === 1 && hasAnalysis && (
            <div>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '600' }}>
                  Overall Purpose
                </h3>
                <p style={{ margin: 0, fontSize: '16px', lineHeight: '1.7' }}>
                  {snippet.aiAnalysis.overallPurpose}
                </p>
              </div>

              {snippet.aiAnalysis.keyTechniques && snippet.aiAnalysis.keyTechniques.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', margin: '0 0 12px 0' }}>
                    🔑 Key Techniques
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: '24px', color: '#4a5568' }}>
                    {snippet.aiAnalysis.keyTechniques.map((technique, index) => (
                      <li key={index} style={{ marginBottom: '8px', fontSize: '16px', lineHeight: '1.6' }}>
                        {technique}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {snippet.aiAnalysis.bestPractices && snippet.aiAnalysis.bestPractices.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', margin: '0 0 12px 0' }}>
                    ✅ Best Practices
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: '24px', color: '#4a5568' }}>
                    {snippet.aiAnalysis.bestPractices.map((practice, index) => (
                      <li key={index} style={{ marginBottom: '8px', fontSize: '16px', lineHeight: '1.6' }}>
                        {practice}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {snippet.aiAnalysis.potentialImprovements && snippet.aiAnalysis.potentialImprovements.length > 0 && (
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#2d3748', margin: '0 0 12px 0' }}>
                    💡 Potential Improvements
                  </h3>
                  <ul style={{ margin: 0, paddingLeft: '24px', color: '#4a5568' }}>
                    {snippet.aiAnalysis.potentialImprovements.map((improvement, index) => (
                      <li key={index} style={{ marginBottom: '8px', fontSize: '16px', lineHeight: '1.6' }}>
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Interactive Explainer */}
          {tabValue === 4 && hasExplanations && (
            <CodeExplainer snippet={snippet} />
          )}
        </div>
      </div>

      {/* AI Chat Assistant */}
      {snippet && (
        <AIChatAssistant 
          assetId={snippet._id}
          chapterId={snippet._id}
          isDarkMode={theme === 'dark'}
          isOpen={isChatbotOpen}
          onOpenChange={setIsChatbotOpen}
          assignmentContext={{
            snippetId: snippet._id,
            snippetName: snippet.name,
            programmingLanguage: snippet.programmingLanguage,
            code: snippet.code,
            description: snippet.description,
            difficulty: snippet.difficulty,
            tags: snippet.tags,
            hasExplanations: hasExplanations,
            currentTab: tabValue === 0 ? 'Code Only' : 
                        tabValue === 1 ? 'Code Analysis' : 
                        'Interactive Explainer'
          }}
        />
      )}
    </div>
  );
};

export default UserCodeSnippetViewer;
